﻿<script setup lang="ts">
    import { computed } from 'vue';
    import List from '@shared/components/List/List.vue';
    import { setupIssues } from './Issues';
    import Preview from '@shared/components/Preview.vue';
    import ListItem from './ListItem.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { type IssueStatistics, IssueSourceFilter } from './interfaces';

    const {
        state,
        switchTab,
        updateCheckedItems,
        updateSendingCount,
        updateSentCount,
        handleDelete,
        handlePauseIssue,
        handlePausingIssue,
        handleMoveIssueToDrafts,
        handleMoveIssueToSent,
        handleIssueStatisticsLoaded,
        handleLoadCategories,
        dateRangeChanged,
        handleUpdateOrder,
        categoryUpdated,
        toggleSourceFilter,
        showPreview,
        fromAddressUpdated,
        filterUpdated,
        orderOptions,
        filterOptions,
        loadMore,
        clearSelection,
        itemsNotDeleted,
        disabledCheckboxes,
        handleDisableCheckboxes,
        getTotalCount,
        tags,
    } = setupIssues();

    const hasNoConnectionFiltering = computed(() => {
        return state.sourceFilter === 0 && state.selectedCategoryIds.length === 0 && state.fromAddress === '';
    });

    const clearFilters = () => {
        toggleSourceFilter(IssueSourceFilter.None);
        categoryUpdated([]);
        fromAddressUpdated('');
    };
</script>

<template>
    <List
        :items="itemsNotDeleted"
        :itemId="'IssueId'"
        :title="$Resources.IssuesHeading"
        :description="state.currentTabData?.description"
        :tabs="state.tabs"
        :checkedItems="state.isCreateNew ? null : state.selected"
        :currentTab="state.currentTabData?.name"
        :serverSideFilter="true"
        :tags="tags"
        :filterKeys="filterOptions()"
        :orderOptions="orderOptions(state.currentTabData?.name)"
        :orderByValue="state.orderByValue"
        :orderByDirection="state.orderByDirection"
        :loading="state.loading"
        :totalItemCount="getTotalCount"
        :all-loaded="state.issues.length === getTotalCount || state.allLoaded"
        :recycleManager="state.isCreateNew ? false : true"
        :disableCheckboxes="handleDisableCheckboxes"
        :disabledCheckboxes="disabledCheckboxes"
        :deleteConfirmation="true"
        :dateFilter="state.dateFilter"
        :hasExternalFilters="!hasNoConnectionFiltering"
        @update:order="handleUpdateOrder"
        @update:filter="filterUpdated"
        @update:tab="switchTab"
        @update:checkedItems="updateCheckedItems"
        @clearSelected="clearSelection"
        @loadMore="loadMore"
        @deleteSelected="handleDelete"
        @onDateRangeChange="dateRangeChanged"
        @clear:externalFilters="clearFilters">
        <template #toolbar>
            <router-link :to="{ name: 'IssuesNew' }" class="btn btn-default" v-show="state.selected.length === 0">
                <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                <span class="navbar-label">{{ $Resources.IssuesNewDraftButton }}</span>
            </router-link>

            <BSDropdownButton
                menuClass="dropdown-menu-right"
                v-show="state.selected.length === 0"
                @onShow="handleLoadCategories"
                :disableCloseOnClickClassList="['drop-mask']">
                <template #toggle>
                    <span class="glyphicon glyphicon-filter m-xxs-r"></span>
                    {{ $Resources.FilterHeading }}
                </template>
                <template #content>
                    <li class="dropdown-menu-content">
                        <h4 class="m-0-t">{{ $Resources.FilterHeading }}</h4>

                        <h5 class="label-look m-sm-b">{{ $Resources.ToggleOptionsHeading }}:</h5>
                        <div class="form-group" :class="!hasNoConnectionFiltering ? 'has-selected' : ''">
                            <FormCheckable
                                class="m-sm-b"
                                :switchStyle="SwitchStyle.Standard"
                                :label="$Resources.ToggleOptionsNoConnection"
                                :checked="(state.sourceFilter & IssueSourceFilter.NoConnection) !== 0"
                                @update:modelValue="toggleSourceFilter(IssueSourceFilter.NoConnection)" />
                            <FormCheckable
                                class="m-sm-b"
                                :switchStyle="SwitchStyle.Standard"
                                :label="$Resources.ToggleOptionsEventRelated"
                                :checked="(state.sourceFilter & IssueSourceFilter.EventConnected) !== 0"
                                @update:modelValue="toggleSourceFilter(IssueSourceFilter.EventConnected)" />
                            <FormCheckable
                                class="m-sm-b"
                                :switchStyle="SwitchStyle.Standard"
                                :label="$Resources.ToggleOptionsJourneyRelated"
                                :checked="(state.sourceFilter & IssueSourceFilter.JourneyConnected) !== 0"
                                @update:modelValue="toggleSourceFilter(IssueSourceFilter.JourneyConnected)" />
                            <FormCheckable
                                :switchStyle="SwitchStyle.Standard"
                                :label="$Resources.ToggleOptionsSurveyRelated"
                                :checked="(state.sourceFilter & IssueSourceFilter.SurveyConnected) !== 0"
                                @update:modelValue="toggleSourceFilter(IssueSourceFilter.SurveyConnected)" />
                        </div>

                        <MultiSelect
                            class="m-sm-b"
                            v-show="state.categories !== null"
                            :loading="state.loadingCategories"
                            :items="state.categories"
                            :selectedItems="state.selectedCategoryIds"
                            :label="$Resources.CategoryLabel"
                            displayProp="Name"
                            selectProp="CategoryId"
                            selectOnly="CategoryId"
                            :maxSelectable="1"
                            @update:selectedItems="categoryUpdated"></MultiSelect>

                        <FormInput
                            :label="$Resources.FromAddressLabel"
                            :placeholder="$Resources.FromAddressPlaceholder"
                            :modelValue="state.fromAddress"
                            @update:modelValue="fromAddressUpdated"></FormInput>
                    </li>
                </template>
            </BSDropdownButton>
        </template>
        <template #item="{ item, index }">
            <ListItem
                :issue="item"
                :tags="tags"
                :isCreateNew="state.isCreateNew"
                :currentTab="state.currentTabData!.name"
                :loading="state.loading"
                :storedIssueStatistics="state.storedIssueStatistics"
                @showPreview="showPreview(item.IssueId)"
                @clickedIssueId="$emit('clickedIssueId', item.IssueId)"
                @pauseIssue="handlePauseIssue(item.IssueId)"
                @pauseRunningIssue="handlePausingIssue(item.IssueId)"
                @moveIssueToDrafts="handleMoveIssueToDrafts(item.IssueId)"
                @moveIssueToSent="handleMoveIssueToSent(item.IssueId)"
                @issueStatisticsLoaded="(newVal: IssueStatistics) => handleIssueStatisticsLoaded(newVal)"
                @updateSendingCount="updateSendingCount"
                @updateSentCount="updateSentCount"></ListItem>
        </template>
    </List>

    <Preview :previewUrl="state.previewUrl" @close="state.previewUrl = null" />
</template>
